import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  DocumentTextIcon,
  EyeIcon,
  ArrowPathIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import {
  AdminApi,
  AdminEnterpriseBatchItem,
  AdminEnterpriseBatchDetail
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const fmtMoney = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')} đ`

const AdminEnterpriseBatchListPage: React.FC = () => {
  const [items, setItems] = useState<AdminEnterpriseBatchItem[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [publishingId, setPublishingId] = useState<number | null>(null)

  // Modal xem chi tiết lô
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)
  const [batchDetail, setBatchDetail] = useState<AdminEnterpriseBatchDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await AdminApi.getEnterpriseBatches({
        page,
        limit: 15,
        search: searchTerm || undefined
      })
      if (res.success) {
        setItems(res.data.items)
        setTotalPages(res.data.pagination.totalPages || 1)
        setTotal(res.data.pagination.total || 0)
      } else {
        toast.error('Không tải được danh sách lô đăng ký')
      }
    } catch {
      toast.error('Lỗi khi tải danh sách lô đăng ký DN')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearchTerm(search.trim())
  }

  const handleClearSearch = () => {
    setSearch('')
    setSearchTerm('')
    setPage(1)
  }

  const handlePublishBatch = async (batch: AdminEnterpriseBatchItem) => {
    const confirmMsg =
      `Xác nhận xuất 01 hóa đơn MISA tổng cho toàn bộ đăng ký trong Lô #${batch.id}?\n\n` +
      `- Doanh nghiệp: ${batch.enterpriseName}\n` +
      `- Mã số thuế: ${batch.taxCode}\n` +
      `- Email nhận HĐ: ${batch.invoiceEmail}\n` +
      `- Số lượng học viên: ${batch.totalRegistrations} (Hợp lệ: ${batch.successCount})\n` +
      `- Tổng số tiền: ${fmtMoney(batch.totalAmount)}\n\n` +
      `Hóa đơn sẽ được ký số và gửi trực tiếp sang hệ thống MISA meInvoice.`

    if (!window.confirm(confirmMsg)) return

    setPublishingId(batch.id)
    try {
      const res = await AdminApi.publishMisaForEnterpriseBatch(batch.id)
      if (res.success) {
        toast.success(`Đã xuất hóa đơn MISA cho Lô #${batch.id} thành công!`)
        loadData()
      } else {
        toast.error(res.message || 'Xuất hóa đơn cho lô thất bại')
      }
    } catch (e: any) {
      const errorMsg = e.response?.data?.message || 'Có lỗi xảy ra khi gọi dịch vụ MISA'
      toast.error(errorMsg)
    } finally {
      setPublishingId(null)
    }
  }

  const openBatchDetail = async (batchId: number) => {
    setSelectedBatchId(batchId)
    setLoadingDetail(true)
    setBatchDetail(null)
    try {
      const res = await AdminApi.getEnterpriseBatchDetail(batchId)
      if (res.success) {
        setBatchDetail(res.data)
      } else {
        toast.error('Không tải được chi tiết lô')
      }
    } catch {
      toast.error('Lỗi khi tải chi tiết lô đăng ký')
    } finally {
      setLoadingDetail(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Navigation tabs between individual registrations and enterprise batches */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-m2-outline-variant pb-4">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/registrations"
              className="text-lg font-medium text-m2-on-surface-variant hover:text-m2-primary"
            >
              Danh sách đăng ký cá nhân
            </Link>
            <span className="text-m2-outline">|</span>
            <span className="text-xl font-bold text-m2-primary border-b-2 border-m2-primary pb-1">
              Lô đăng ký doanh nghiệp
            </span>
          </div>
          <p className="text-sm text-m2-on-surface-variant mt-1">
            Quản lý các lô đăng ký cho nhân viên doanh nghiệp và phát hành hóa đơn MISA tổng theo lô.
          </p>
        </div>

        <Link
          to="/admin/enterprise-registrations/new"
          className="inline-flex items-center gap-2 rounded-lg bg-m2-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-m2-primary/90 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Tạo lô đăng ký DN mới
        </Link>
      </div>

      {/* Filter and Search Bar */}
      <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3 rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="flex-1 min-w-[260px]">
          <input
            type="text"
            className="w-full rounded border border-m2-outline px-3 py-2 text-sm focus:border-m2-primary focus:outline-none"
            placeholder="Tìm theo tên doanh nghiệp hoặc mã số thuế..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="rounded bg-m2-primary px-4 py-2 text-sm font-medium text-white hover:bg-m2-primary/90 transition-colors"
        >
          Tìm kiếm
        </button>
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="rounded border border-m2-outline px-3 py-2 text-sm text-m2-on-surface-variant hover:bg-m2-surface-high transition-colors"
          >
            Xóa tìm kiếm
          </button>
        )}
        <button
          type="button"
          onClick={loadData}
          title="Làm mới"
          className="rounded border border-m2-outline p-2 text-m2-on-surface-variant hover:bg-m2-surface-high transition-colors"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <div className="text-xs text-m2-on-surface-variant ml-auto">
          Tổng số lô: <strong>{total}</strong>
        </div>
      </form>

      {/* Table list */}
      <div className="overflow-x-auto rounded-lg border border-m2-outline-variant bg-m2-surface shadow-sm">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-m2-outline-variant bg-m2-surface-container font-semibold text-m2-on-surface">
              <th className="px-4 py-3">Mã Lô</th>
              <th className="px-4 py-3">Doanh nghiệp</th>
              <th className="px-4 py-3">Ngày tạo</th>
              <th className="px-4 py-3">Số lượng học viên</th>
              <th className="px-4 py-3 text-right">Tổng tiền</th>
              <th className="px-4 py-3 text-center">Hóa đơn MISA</th>
              <th className="px-4 py-3 text-center">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-m2-outline-variant">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-m2-on-surface-variant">
                  <div className="inline-flex items-center gap-2">
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    <span>Đang tải dữ liệu...</span>
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-m2-on-surface-variant">
                  Chưa có lô đăng ký doanh nghiệp nào
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const canPublish = row.invoiceStatus !== 'SUCCESS' && row.totalRegistrations > 0
                return (
                  <tr key={row.id} className="hover:bg-m2-surface-container/50 transition-colors">
                    <td className="px-4 py-3 font-mono font-semibold text-m2-primary">
                      #{row.id}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-m2-on-surface">{row.enterpriseName}</div>
                      <div className="text-xs text-m2-on-surface-variant">
                        MST: <span className="font-mono">{row.taxCode}</span>
                      </div>
                      <div className="text-xs text-m2-on-surface-variant">
                        Email: <span>{row.invoiceEmail}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-m2-on-surface-variant whitespace-nowrap">
                      {formatDbDateTimeVi(row.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.totalRegistrations} học viên</div>
                      <div className="text-xs text-m2-on-surface-variant">
                        {row.giftCount > 0 && <span className="text-amber-800">Tặng {row.giftCount} | </span>}
                        Đạt chuẩn: {row.successCount}/{row.totalRegistrations}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-m2-on-surface whitespace-nowrap">
                      {fmtMoney(row.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.invoiceStatus === 'SUCCESS' ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                            <CheckCircleIcon className="h-4 w-4" />
                            Đã xuất HĐ
                          </span>
                          {row.misaInvNo && (
                            <span className="mt-1 text-xs font-mono font-bold text-emerald-900">
                              Số: {row.misaInvNo}
                            </span>
                          )}
                          {row.misaInvoiceDate && (
                            <span className="text-[11px] text-m2-on-surface-variant">
                              Ngày: {row.misaInvoiceDate}
                            </span>
                          )}
                        </div>
                      ) : row.invoiceStatus === 'FAIL' ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            Lỗi xuất HĐ
                          </span>
                          {row.misaError && (
                            <span
                              className="mt-1 text-[11px] text-red-700 max-w-[160px] truncate"
                              title={row.misaError}
                            >
                              {row.misaError}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                          Chưa xuất HĐ
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {canPublish && (
                          <button
                            type="button"
                            disabled={publishingId === row.id}
                            onClick={() => handlePublishBatch(row)}
                            className="inline-flex items-center gap-1 rounded bg-emerald-700 px-2.5 py-1 text-xs font-semibold text-white shadow hover:bg-emerald-800 disabled:opacity-50 transition-colors"
                            title="Xuất 01 hóa đơn MISA cho toàn bộ đăng ký trong lô này"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            {publishingId === row.id ? 'Đang xuất...' : 'Xuất hóa đơn cho lô'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openBatchDetail(row.id)}
                          className="inline-flex items-center gap-1 rounded border border-m2-outline px-2.5 py-1 text-xs font-medium text-m2-on-surface hover:bg-m2-surface-high transition-colors"
                          title="Xem danh sách học viên trong lô"
                        >
                          <EyeIcon className="h-4 w-4" />
                          Chi tiết
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-m2-on-surface-variant">
            Trang {page} / {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-m2-outline px-3 py-1 hover:bg-m2-surface-high disabled:opacity-40"
            >
              Trước
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-m2-outline px-3 py-1 hover:bg-m2-surface-high disabled:opacity-40"
            >
              Sau
            </button>
          </div>
        </div>
      )}

      {/* Modal chi tiết học viên trong lô */}
      {selectedBatchId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-m2-outline-variant pb-4">
              <div>
                <h2 className="text-lg font-bold text-m2-on-surface">
                  Chi tiết Lô đăng ký #{selectedBatchId}
                </h2>
                {batchDetail?.batch && (
                  <p className="text-sm text-m2-on-surface-variant mt-1">
                    Doanh nghiệp: <strong>{batchDetail.batch.enterpriseName}</strong> (MST: {batchDetail.batch.taxCode})
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedBatchId(null)}
                className="rounded-lg p-1 text-m2-on-surface-variant hover:bg-m2-surface-high"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-4">
              {loadingDetail ? (
                <div className="py-12 text-center text-m2-on-surface-variant">
                  <ArrowPathIcon className="mx-auto h-6 w-6 animate-spin mb-2" />
                  Đang tải danh sách học viên...
                </div>
              ) : batchDetail ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 rounded-lg bg-m2-surface-container p-3 text-xs md:grid-cols-4">
                    <div>
                      <span className="text-m2-on-surface-variant">Email nhận HĐ:</span>
                      <p className="font-semibold text-m2-on-surface">{batchDetail.batch.invoiceEmail}</p>
                    </div>
                    <div>
                      <span className="text-m2-on-surface-variant">Địa chỉ DN:</span>
                      <p className="font-semibold text-m2-on-surface">{batchDetail.batch.address}</p>
                    </div>
                    <div>
                      <span className="text-m2-on-surface-variant">Thời gian tạo:</span>
                      <p className="font-semibold text-m2-on-surface">{formatDbDateTimeVi(batchDetail.batch.createdAt)}</p>
                    </div>
                    <div>
                      <span className="text-m2-on-surface-variant">Người lập lô:</span>
                      <p className="font-semibold text-m2-on-surface">{batchDetail.batch.createdByName || '-'}</p>
                    </div>
                  </div>

                  <h3 className="font-semibold text-sm text-m2-on-surface">
                    Danh sách học viên trong lô ({batchDetail.registrations.length})
                  </h3>

                  <div className="overflow-x-auto rounded border border-m2-outline-variant">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="bg-m2-surface-container font-semibold text-m2-on-surface border-b border-m2-outline-variant">
                          <th className="px-3 py-2">STT</th>
                          <th className="px-3 py-2">Mã ĐK</th>
                          <th className="px-3 py-2">Học viên</th>
                          <th className="px-3 py-2">Email / SĐT</th>
                          <th className="px-3 py-2">Khóa học / Lớp</th>
                          <th className="px-3 py-2 text-right">Học phí</th>
                          <th className="px-3 py-2 text-center">Trạng thái</th>
                          <th className="px-3 py-2 text-center">Hóa đơn</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-m2-outline-variant">
                        {batchDetail.registrations.map((reg, idx) => (
                          <tr key={reg.id} className="hover:bg-m2-surface-container/30">
                            <td className="px-3 py-2 text-center text-m2-on-surface-variant">{idx + 1}</td>
                            <td className="px-3 py-2 font-mono font-semibold text-m2-primary">{reg.code}</td>
                            <td className="px-3 py-2 font-medium">{reg.studentName}</td>
                            <td className="px-3 py-2 text-m2-on-surface-variant">
                              <div>{reg.studentEmail}</div>
                              {reg.studentPhone && <div>{reg.studentPhone}</div>}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{reg.courseCode} - {reg.courseName}</div>
                              <div className="text-m2-on-surface-variant">Lớp: {reg.classroomCode}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-medium whitespace-nowrap">
                              {reg.isGift ? (
                                <span className="text-amber-800 font-semibold">Tặng KM (0 đ)</span>
                              ) : (
                                fmtMoney(reg.fee)
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                                {reg.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {reg.misaInvNo ? (
                                <span className="font-mono font-semibold text-emerald-700">
                                  {reg.misaInvNo}
                                </span>
                              ) : (
                                <span className="text-m2-on-surface-variant">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedBatchId(null)}
                className="rounded-lg bg-m2-surface-container px-4 py-2 text-sm font-semibold text-m2-on-surface hover:bg-m2-surface-high transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminEnterpriseBatchListPage
